import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { reachService } from '../../services/reachService';

const SUGGESTED_MODES = ['By Car', 'By Bus', 'By Train', 'By Flight', 'By Taxi'];

const emptyCityForm    = { city_name: '', is_active: true };
const emptyTransportForm = { mode: '', description: '', is_active: true };

const ManageHowToReach = () => {
  const [cities, setCities]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState({});   // { cityId: true/false }

  // City modal
  const [showCityModal, setShowCityModal] = useState(false);
  const [editCity, setEditCity]           = useState(null);
  const [cityForm, setCityForm]           = useState(emptyCityForm);
  const [cityError, setCityError]         = useState('');
  const [savingCity, setSavingCity]       = useState(false);

  // Transport modal
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [editTransport, setEditTransport]           = useState(null);
  const [activeCityId, setActiveCityId]             = useState(null);
  const [transportForm, setTransportForm]           = useState(emptyTransportForm);
  const [transportError, setTransportError]         = useState('');
  const [savingTransport, setSavingTransport]       = useState(false);

  const load = () => {
    setLoading(true);
    reachService.getAllCities()
      .then(res => {
        const data = res.data.cities || [];
        setCities(data);
        // keep all cities expanded by default
        const exp = {};
        data.forEach(c => { exp[c.id] = true; });
        setExpanded(prev => ({ ...exp, ...prev }));
      })
      .catch(() => toast.error('Failed to load entries.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── City modal helpers ────────────────────────────────────────────────────

  const openAddCity = () => {
    setEditCity(null);
    setCityForm(emptyCityForm);
    setCityError('');
    setShowCityModal(true);
  };

  const openEditCity = (city) => {
    setEditCity(city);
    setCityForm({ city_name: city.city_name, is_active: city.is_active });
    setCityError('');
    setShowCityModal(true);
  };

  const handleSaveCity = async () => {
    if (!cityForm.city_name.trim()) { setCityError('City name is required.'); return; }
    setSavingCity(true);
    setCityError('');
    try {
      if (editCity) {
        await reachService.updateCity(editCity.id, cityForm);
        toast.success('City updated.');
      } else {
        await reachService.createCity(cityForm);
        toast.success('City added.');
      }
      setShowCityModal(false);
      load();
    } catch (err) {
      setCityError(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSavingCity(false);
    }
  };

  const doDeleteCity = async (id, name) => {
    try {
      await reachService.deleteCity(id);
      setCities(prev => prev.filter(c => c.id !== id));
      toast.success(`"${name}" deleted.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete.');
    }
  };

  const handleDeleteCity = (id, name) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: '500' }}>Delete "{name}" and all its transport entries?</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { toast.dismiss(t.id); doDeleteCity(id, name); }}
            style={s.toastDelete}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)} style={s.toastCancel}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const handleToggleCity = async (city) => {
    try {
      await reachService.updateCity(city.id, { is_active: !city.is_active });
      setCities(prev => prev.map(c => c.id === city.id ? { ...c, is_active: !c.is_active } : c));
      toast.success(city.is_active ? 'City hidden from public.' : 'City visible to public.');
    } catch {
      toast.error('Failed to update.');
    }
  };

  // ── Transport modal helpers ───────────────────────────────────────────────

  const openAddTransport = (cityId) => {
    setEditTransport(null);
    setActiveCityId(cityId);
    setTransportForm(emptyTransportForm);
    setTransportError('');
    setShowTransportModal(true);
  };

  const openEditTransport = (transport) => {
    setEditTransport(transport);
    setActiveCityId(transport.city_id);
    setTransportForm({
      mode: transport.mode,
      description: transport.description,
      is_active: transport.is_active,
    });
    setTransportError('');
    setShowTransportModal(true);
  };

  const handleSaveTransport = async () => {
    if (!transportForm.mode.trim())        { setTransportError('Mode is required.'); return; }
    if (!transportForm.description.trim()) { setTransportError('Description is required.'); return; }
    setSavingTransport(true);
    setTransportError('');
    try {
      if (editTransport) {
        await reachService.updateTransport(editTransport.id, transportForm);
        toast.success('Transport updated.');
      } else {
        await reachService.createTransport(activeCityId, transportForm);
        toast.success('Transport added.');
      }
      setShowTransportModal(false);
      load();
    } catch (err) {
      setTransportError(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSavingTransport(false);
    }
  };

  const doDeleteTransport = async (id, mode) => {
    try {
      await reachService.deleteTransport(id);
      setCities(prev => prev.map(c => ({
        ...c,
        transports: c.transports.filter(t => t.id !== id),
      })));
      toast.success(`"${mode}" deleted.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete.');
    }
  };

  const handleDeleteTransport = (id, mode) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: '500' }}>Delete "{mode}"?</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { toast.dismiss(t.id); doDeleteTransport(id, mode); }}
            style={s.toastDelete}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)} style={s.toastCancel}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const handleToggleTransport = async (transport) => {
    try {
      await reachService.updateTransport(transport.id, { is_active: !transport.is_active });
      setCities(prev => prev.map(c => ({
        ...c,
        transports: c.transports.map(t =>
          t.id === transport.id ? { ...t, is_active: !t.is_active } : t
        ),
      })));
      toast.success(transport.is_active ? 'Hidden from public.' : 'Visible to public.');
    } catch {
      toast.error('Failed to update.');
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.container}>
      <div style={s.topBar}>
        <div>
          <h1 style={s.pageTitle}>How to Reach</h1>
          <p style={s.pageSubtitle}>{cities.length} source {cities.length === 1 ? 'city' : 'cities'} configured</p>
        </div>
        <button style={s.addBtn} onClick={openAddCity}><Plus size={18} /> Add City</button>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading...</p>
      ) : cities.length === 0 ? (
        <div style={s.empty}>
          <MapPin size={64} color="#d1d5db" />
          <p style={s.emptyText}>No cities added yet. Add your first departure city!</p>
          <button style={{ ...s.addBtn, marginTop: 16 }} onClick={openAddCity}><Plus size={16} /> Add City</button>
        </div>
      ) : (
        <div style={s.list}>
          {cities.map(city => (
            <div key={city.id} style={{ ...s.cityCard, opacity: city.is_active ? 1 : 0.6 }}>
              {/* City header row */}
              <div style={s.cityRow}>
                <button style={s.expandBtn} onClick={() => toggleExpand(city.id)}>
                  {expanded[city.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <MapPin size={16} color="#16a34a" style={{ flexShrink: 0 }} />
                <span style={s.cityName}>{city.city_name}</span>
                <span style={{ ...s.badge, ...(city.is_active ? s.badgeActive : s.badgeInactive) }}>
                  {city.is_active ? 'Active' : 'Inactive'}
                </span>
                <span style={s.transportCount}>
                  {city.transports?.length || 0} mode{(city.transports?.length || 0) !== 1 ? 's' : ''}
                </span>
                <div style={s.cityActions}>
                  <button style={s.toggleBtn} title={city.is_active ? 'Hide' : 'Show'}
                    onClick={() => handleToggleCity(city)}>
                    {city.is_active ? 'Hide' : 'Show'}
                  </button>
                  <button style={s.editBtn} title="Edit city" onClick={() => openEditCity(city)}>
                    <Edit2 size={14} />
                  </button>
                  <button style={s.addModeBtn} title="Add transport mode"
                    onClick={() => { setExpanded(prev => ({ ...prev, [city.id]: true })); openAddTransport(city.id); }}>
                    <Plus size={14} /> Mode
                  </button>
                  <button style={s.deleteBtn} title="Delete city" onClick={() => handleDeleteCity(city.id, city.city_name)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Transport rows */}
              {expanded[city.id] && (
                <div style={s.transportList}>
                  {(!city.transports || city.transports.length === 0) ? (
                    <div style={s.noTransports}>
                      No transport modes yet.{' '}
                      <button style={s.inlineAddBtn} onClick={() => openAddTransport(city.id)}>
                        + Add one
                      </button>
                    </div>
                  ) : (
                    city.transports.map(t => (
                      <div key={t.id} style={{ ...s.transportRow, opacity: t.is_active ? 1 : 0.55 }}>
                        <div style={s.transportLeft}>
                          <span style={s.modeTag}>{t.mode}</span>
                          <span style={{ ...s.badge, ...(t.is_active ? s.badgeActive : s.badgeInactive), fontSize: 11 }}>
                            {t.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <p style={s.transportDesc}>{t.description}</p>
                        </div>
                        <div style={s.transportActions}>
                          <button style={s.toggleBtn} onClick={() => handleToggleTransport(t)}>
                            {t.is_active ? 'Hide' : 'Show'}
                          </button>
                          <button style={s.editBtn} onClick={() => openEditTransport(t)}><Edit2 size={14} /></button>
                          <button style={s.deleteBtn} onClick={() => handleDeleteTransport(t.id, t.mode)}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── City Modal ─────────────────────────────────────────────────────── */}
      {showCityModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editCity ? 'Edit City' : 'Add City'}</h2>
              <button style={s.closeBtn} onClick={() => setShowCityModal(false)}><X size={20} /></button>
            </div>
            {cityError && <div style={s.errorBox}>{cityError}</div>}
            <div style={s.inputGroup}>
              <label style={s.label}>City Name</label>
              <input style={s.input} type="text" placeholder="e.g. Delhi"
                value={cityForm.city_name}
                onChange={e => setCityForm({ ...cityForm, city_name: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleSaveCity()} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <label style={{ ...s.label, margin: 0 }}>Active</label>
              <button
                style={{ ...s.toggleSwitch, backgroundColor: cityForm.is_active ? '#16a34a' : '#d1d5db' }}
                onClick={() => setCityForm({ ...cityForm, is_active: !cityForm.is_active })}>
                <span style={{ ...s.toggleThumb, left: cityForm.is_active ? 22 : 2 }} />
              </button>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{cityForm.is_active ? 'Visible on site' : 'Hidden from site'}</span>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtnModal} onClick={() => setShowCityModal(false)}>Cancel</button>
              <button style={{ ...s.saveBtn, opacity: savingCity ? 0.7 : 1 }}
                onClick={handleSaveCity} disabled={savingCity}>
                <Save size={16} /> {savingCity ? 'Saving...' : editCity ? 'Update City' : 'Add City'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transport Modal ────────────────────────────────────────────────── */}
      {showTransportModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editTransport ? 'Edit Transport Mode' : 'Add Transport Mode'}</h2>
              <button style={s.closeBtn} onClick={() => setShowTransportModal(false)}><X size={20} /></button>
            </div>
            {transportError && <div style={s.errorBox}>{transportError}</div>}
            <div style={s.inputGroup}>
              <label style={s.label}>Transport Mode</label>
              <input style={s.input} type="text" placeholder="e.g. By Car"
                list="mode-suggestions"
                value={transportForm.mode}
                onChange={e => setTransportForm({ ...transportForm, mode: e.target.value })} />
              <datalist id="mode-suggestions">
                {SUGGESTED_MODES.map(m => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Directions / Description</label>
              <textarea style={{ ...s.input, minHeight: 120, resize: 'vertical' }}
                placeholder="Describe how to reach using this mode..."
                value={transportForm.description}
                onChange={e => setTransportForm({ ...transportForm, description: e.target.value })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <label style={{ ...s.label, margin: 0 }}>Active</label>
              <button
                style={{ ...s.toggleSwitch, backgroundColor: transportForm.is_active ? '#16a34a' : '#d1d5db' }}
                onClick={() => setTransportForm({ ...transportForm, is_active: !transportForm.is_active })}>
                <span style={{ ...s.toggleThumb, left: transportForm.is_active ? 22 : 2 }} />
              </button>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{transportForm.is_active ? 'Visible on site' : 'Hidden from site'}</span>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtnModal} onClick={() => setShowTransportModal(false)}>Cancel</button>
              <button style={{ ...s.saveBtn, opacity: savingTransport ? 0.7 : 1 }}
                onClick={handleSaveTransport} disabled={savingTransport}>
                <Save size={16} /> {savingTransport ? 'Saving...' : editTransport ? 'Update Mode' : 'Add Mode'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container:      { padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' },
  topBar:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle:      { fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '6px' },
  pageSubtitle:   { fontSize: '16px', color: '#6b7280' },
  addBtn:         { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  list:           { display: 'flex', flexDirection: 'column', gap: 12 },
  cityCard:       { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'hidden', transition: 'opacity 0.2s' },
  cityRow:        { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' },
  expandBtn:      { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 },
  cityName:       { fontSize: 16, fontWeight: 700, color: '#111827', flexGrow: 1 },
  transportCount: { fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' },
  badge:          { display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  badgeActive:    { backgroundColor: '#d1fae5', color: '#065f46' },
  badgeInactive:  { backgroundColor: '#f3f4f6', color: '#6b7280' },
  cityActions:    { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 },
  toggleBtn:      { padding: '5px 12px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  editBtn:        { padding: '6px 10px', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  addModeBtn:     { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1.5px solid #bbf7d0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  deleteBtn:      { padding: '6px 10px', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  transportList:  { padding: '8px 18px 12px 48px' },
  noTransports:   { fontSize: 13, color: '#9ca3af', padding: '8px 0' },
  inlineAddBtn:   { background: 'none', border: 'none', color: '#16a34a', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 },
  transportRow:   { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6', transition: 'opacity 0.2s' },
  transportLeft:  { flex: 1, minWidth: 0 },
  modeTag:        { display: 'inline-block', padding: '3px 12px', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: 20, fontSize: 13, fontWeight: 600, marginRight: 8, marginBottom: 4 },
  transportDesc:  { fontSize: 13, color: '#4b5563', marginTop: 6, lineHeight: 1.55, whiteSpace: 'pre-line' },
  transportActions: { display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, paddingTop: 2 },
  empty:          { textAlign: 'center', padding: '60px 20px', backgroundColor: 'white', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  emptyText:      { fontSize: '18px', color: '#9ca3af', marginTop: '16px' },
  overlay:        { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:          { backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle:     { fontSize: '22px', fontWeight: '600', color: '#111827' },
  closeBtn:       { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' },
  errorBox:       { padding: '12px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  inputGroup:     { marginBottom: '16px' },
  label:          { display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:          { width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Poppins', sans-serif", color: '#1f2937', backgroundColor: '#fff' },
  toggleSwitch:   { position: 'relative', width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', flexShrink: 0, transition: 'background-color 0.2s' },
  toggleThumb:    { position: 'absolute', top: 3, width: 18, height: 18, backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
  modalFooter:    { display: 'flex', gap: '12px', marginTop: '8px' },
  cancelBtnModal: { flex: 1, padding: '12px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  saveBtn:        { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  toastDelete:    { padding: '6px 14px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  toastCancel:    { padding: '6px 14px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
};

export default ManageHowToReach;
